import React from 'react';
import { X, Car, DollarSign, Calendar, MapPin, Fuel, Settings, Clock } from 'lucide-react';
import CarThumbnail from './CarThumbnail';
import PriceChart from './PriceChart';

const CarModal = ({ car, isOpen, onClose }) => {
  if (!isOpen || !car) return null;

  const formatPrice = (price, currency = 'USD') => {
    const formattedNumber = new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 0,
    }).format(price);
    
    if (currency === 'ARS') {
      return `$ ${formattedNumber}`;
    } else {
      return `US$ ${formattedNumber}`;
    }
  };

  const formatKms = (kms) => {
    return new Intl.NumberFormat('es-AR').format(kms) + ' km';
  };

  const formatDaysOld = (days) => {
    if (!days || days === 0) return 'Nuevo';
    if (days === 1) return '1 día';
    if (days < 30) return `${days} días`;
    if (days < 365) {
      const months = Math.floor(days / 30);
      return months === 1 ? '1 mes' : `${months} meses`;
    }
    const years = Math.floor(days / 365);
    return years === 1 ? '1 año' : `${years} años`;
  };

  // Solo usar datos reales de la base de datos
  const carDetails = {
    año: car.vehicle_year,
    combustible: car.fuel_type,
    transmision: car.transmission,
    ubicacion: car.city
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-[#1c1c1a] rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
        {/* Header */}
        <div className="sticky top-0 bg-[#1c1c1a]/95 backdrop-blur-sm border-b border-gray-700 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Detalles del Vehículo</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#232321] rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Columna izquierda - Imagen y gráfico */}
            <div className="space-y-6">
              {/* Imagen principal */}
              <div className="aspect-video rounded-xl overflow-hidden bg-[#232321]">
                <img
                  src={car.pictures}
                  alt={car.title}
                  className="w-full h-full object-cover object-center"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-full h-full bg-gradient-to-br from-dark-accent via-dark-secondary to-dark-card flex items-center justify-center" style={{display: 'none'}}>
                  <div className="flex flex-col items-center space-y-2">
                    <div className="p-4 bg-dark-text/20 rounded-full">
                      <Car className="w-12 h-12 text-[#7b8fa8]" />
                    </div>
                    <span className="text-sm text-gray-400">Imagen no disponible</span>
                  </div>
                </div>
              </div>

              {/* Gráfico de precios */}
              {car.price_history && car.price_history.length > 1 && 
               car.price_history.some(p => p.price !== car.price_history[0].price) && (
              <div className="glass-effect rounded-xl p-4">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  <span>Evolución de Precio</span>
                </h3>
                <PriceChart 
                  priceHistory={car.price_history} 
                  currentPrice={car.price}
                  currency={car.currency_id}
                />
              </div>
              )}
            </div>

            {/* Columna derecha - Información */}
            <div className="space-y-6">
              {/* Título y precio */}
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  {car.permalink ? (
                    <a 
                      href={car.permalink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:text-[#7b8fa8] transition-colors cursor-pointer inline-flex items-center gap-2"
                      title="Ver en MercadoLibre"
                    >
                      {car.title}
                      <svg className="w-5 h-5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  ) : (
                    car.title
                  )}
                </h1>
                <div className="flex items-center space-x-4">
                  <span className="text-3xl font-bold text-green-400">
                    {formatPrice(car.price, car.currency_id)}
                  </span>
                  <span className="text-lg text-blue-400">
                    {formatKms(car.kilometers)}
                  </span>
                </div>
                {car.permalink && (
                  <div className="mt-2">
                    <a 
                      href={car.permalink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-[#7b8fa8] hover:text-white transition-colors"
                    >
                      <span>Ver en MercadoLibre</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                )}
              </div>

              {/* Información básica - Solo datos reales */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {carDetails.año && (
                  <div className="glass-effect rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Calendar className="w-4 h-4 text-purple-400" />
                      <span className="text-sm text-gray-400">Año</span>
                    </div>
                    <span className="text-lg font-semibold text-white">{carDetails.año}</span>
                  </div>
                )}

                {carDetails.combustible && (
                  <div className="glass-effect rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Fuel className="w-4 h-4 text-orange-400" />
                      <span className="text-sm text-gray-400">Combustible</span>
                    </div>
                    <span className="text-lg font-semibold text-white">{carDetails.combustible}</span>
                  </div>
                )}

                {carDetails.transmision && (
                  <div className="glass-effect rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Settings className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-gray-400">Transmisión</span>
                    </div>
                    <span className="text-lg font-semibold text-white">{carDetails.transmision}</span>
                  </div>
                )}

                {car.days_old && (
                  <div className="glass-effect rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Clock className="w-4 h-4 text-purple-400" />
                      <span className="text-sm text-gray-400">Antigüedad</span>
                    </div>
                    <span className="text-lg font-semibold text-white">{formatDaysOld(car.days_old)}</span>
                  </div>
                )}
              </div>

              {/* Ubicación - Solo si existe */}
              {carDetails.ubicacion && (
                <div className="glass-effect rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <MapPin className="w-4 h-4 text-red-400" />
                    <span className="text-sm text-gray-400">Ubicación</span>
                  </div>
                  <span className="text-white">{carDetails.ubicacion}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarModal;